import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideLayoutGrid,
  LucideUsers,
  LucideSettings2,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { fill as fillPDFProvincialGr78, SCHEMA_TO_PDF_FIELD, PAGE_INDEX_FOR_SCHEMA_FIELD, STEP_ANCHOR_FIELDS } from "./fill.js";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import {
  selectItems,
  twoPlans,
  threePlans,
  fourPlans,
  frenchPlans,
  ersCompleted as ersCompletedOpts,
} from "./options.js";
import { STEPS, STEP_FIELDS, SUBJECT_STEP_IDS, TOP_STEPS, SUB_STEPS, ARTS_ROWS } from "./steps.js";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AICommentButton } from "../shared/AICommentButton";
import { LSWH_CONFIG } from "./aiConfig";
import { FormFieldGrid } from "../shared/FormFieldGrid";

// ─── Reusable primitives ──────────────────────────────────────────────────────

// Compact grade/median column - renders grade + optional median stacked
function GradeColumn({ control, gradeField, medianField, termLabel }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{termLabel}</span>
      <FormField
        control={control}
        name={gradeField}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[8px] text-slate-400 uppercase tracking-wide">Grade</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-10 text-center text-lg font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
      {medianField && (
        <FormField
          control={control}
          name={medianField}
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <span className="text-[8px] text-slate-400 uppercase tracking-wide">Median</span>
              <FormControl>
                <input
                  {...field}
                  maxLength={2}
                  className="w-12 h-9 text-center text-base font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

// Standard subject card: plan pills + grade/median columns + comments
function SubjectCard({ title, planField, grade1Field, median1Field, grade2Field, median2Field, commentsField, plans, nameField, control }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Subject header stripe */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {nameField && (
          <FormField
            control={control}
            name={nameField}
            render={({ field }) => (
              <FormItem className="mb-0">
                <FormControl>
                  <input
                    {...field}
                    placeholder="Subject name"
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white focus:border-teal-400 focus:outline-none w-40"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Plan */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
          <FormField
            control={control}
            name={planField}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PillSelector size="sm" value={field.value} onChange={field.onChange} options={plans} colorMap={{}} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Grades + Medians */}
        {grade1Field && grade2Field && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades & Medians</p>
            <div className="flex items-start gap-4">
              <GradeColumn control={control} gradeField={grade1Field} medianField={median1Field} termLabel="Term 1" />
              <div className="w-4 h-px bg-slate-200 mt-8" />
              <GradeColumn control={control} gradeField={grade2Field} medianField={median2Field} termLabel="Term 2" />
            </div>
          </div>
        )}

        {/* Comments */}
        {commentsField && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Comments</p>
            <FormField
              control={control}
              name={commentsField}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Teacher comments…" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Two-level step indicator: top row (6 nodes) + persistent subject sub-row
function StepIndicator({ currentStep, completedSteps, onStepClick }) {
  const isSubjectStep = SUBJECT_STEP_IDS.has(currentStep);

  return (
    <div>
      {/* ── Top row ───────────────────────────────────────────────────────── */}
      <div className="flex items-start w-full">
        {TOP_STEPS.map((step, index) => {
          const isSubjectsNode = step.id === "subjects";
          const isLast = index === TOP_STEPS.length - 1;
          const isDone = isSubjectsNode
            ? step.stepIds.every((id) => completedSteps.has(id))
            : completedSteps.has(step.id);
          const isCurrent = isSubjectsNode ? isSubjectStep : step.id === currentStep;

          const handleClick = () =>
            isSubjectsNode ? onStepClick(step.jumpTo) : onStepClick(step.id);

          return (
            <div key={String(step.id)} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
              <div className="flex flex-col items-center" style={{ minWidth: "28px" }}>
                <button
                  type="button"
                  onClick={handleClick}
                  title={isSubjectsNode ? "Subjects" : String(step.label)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all cursor-pointer shrink-0 ${
                    isDone && !isCurrent
                      ? "bg-teal-700 border-teal-700 text-white"
                      : isCurrent
                      ? "bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50"
                      : "bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200"
                  }`}
                >
                  {isDone && !isCurrent ? (
                    <LucideCheck size={11} />
                  ) : isSubjectsNode ? (
                    <LucideLayoutGrid size={12} />
                  ) : (
                    step.id
                  )}
                </button>
                <span
                  className={`text-[9px] mt-0.5 text-center leading-tight ${
                    isCurrent ? "text-teal-700 font-bold" : isDone ? "text-teal-500 font-medium" : "text-slate-400 font-medium"
                  }`}
                  style={{ maxWidth: "44px", wordBreak: "break-word" }}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className={`h-px flex-1 mx-0.5 mb-4 ${isDone ? "bg-teal-500" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Subject sub-row ───────────────────────────────────────────────── */}
      <div className="mt-2 mx-1 rounded-xl bg-slate-50/80 border border-slate-100 px-4 py-2">
        <div className="flex items-start w-full">
          {SUB_STEPS.map((step, index) => {
            const isDone = completedSteps.has(step.id);
            const isCurrent = step.id === currentStep;
            const isLast = index === SUB_STEPS.length - 1;
            return (
              <div key={step.id} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
                <div className="flex flex-col items-center" style={{ minWidth: "22px" }}>
                  <button
                    type="button"
                    onClick={() => onStepClick(step.id)}
                    title={step.label}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-[1.5px] transition-all cursor-pointer shrink-0 ${
                      isDone && !isCurrent
                        ? "bg-teal-500 border-teal-500 text-white"
                        : isCurrent
                        ? "bg-teal-500 border-teal-500 text-white scale-125 ring-2 ring-inset ring-teal-300/50"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                  >
                    {isDone && !isCurrent ? <LucideCheck size={8} /> : step.id}
                  </button>
                  <span
                    className={`text-[8px] mt-0.5 text-center leading-tight ${
                      isCurrent ? "text-teal-600 font-bold" : isDone ? "text-teal-400 font-medium" : "text-slate-400 font-medium"
                    }`}
                    style={{ maxWidth: "44px", wordBreak: "break-word" }}
                  >
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-px flex-1 mx-0.5 mb-3.5 ${isDone ? "bg-teal-300" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProvincialGr78202309 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onFormChange, onRequestSave } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const getInitialPage = (step) => step <= 3 ? 1 : step <= 7 ? 2 : 3;
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const saveIntervalRef = useRef(null);
  const isDirtyRef = useRef(false);
  const flushSaveRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Restore saved data and generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDFProvincialGr78(values).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc78 fill]', err));
          })
          .catch(() => fillPDFProvincialGr78(form.getValues()).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc78 fill]', err)))
      );
      return;
    }
    if (defaultsGroupId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-groups/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = { ...form.getValues(), ...(json.reportDefaults || {}) };
            form.reset(values);
            fillPDFProvincialGr78(values).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc78 fill]', err));
          })
          .catch(() => fillPDFProvincialGr78(form.getValues()).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc78 fill]', err)))
      );
      return;
    }
    const saved = localStorage.getItem("formData-provincial-gr7-8-2023-09-v1");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDFProvincialGr78(initialValues).then((url) => setFilledPDF(url)).catch((err) => console.error('[pvrc78 fill]', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets + per-field bounding boxes for yellow highlight
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 3 ? 0 : step <= 7 ? 1 : step <= 9 ? 2 : 3;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_INDEX_FOR_SCHEMA_FIELD[schemaField] ?? 0;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
  }, []);

  // Auto-save (300 ms) + live PDF regeneration with highlight (500 ms)
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (name === '_completedFields') return;

      isDirtyRef.current = true;

      if (name) lastChangedFieldRef.current = name;

      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        try {
          const url = await fillPDFProvincialGr78(form.getValues(), highlightRect);
          setFilledPDF(url);
        } catch (err) {
          console.error('[pvrc78 fill]', err);
        }
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
     
  }, [form]);

  flushSaveRef.current = () => {
    if (!isDirtyRef.current) return;
    isDirtyRef.current = false;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ data: form.getValues() }),
        })
      );
    } else if (defaultsGroupId) {
      onFormChange?.(form.getValues());
    } else {
      localStorage.setItem("formData-provincial-gr7-8-2023-09-v1", JSON.stringify(form.getValues()));
    }
  };

  useEffect(() => {
    saveIntervalRef.current = setInterval(() => flushSaveRef.current?.(), 30_000);
    return () => {
      clearInterval(saveIntervalRef.current);
      flushSaveRef.current?.();
    };
  }, []);  

  const handleDownload = async () => {
    flushSaveRef.current?.();
    if (!filledPDF) return;
    const cleanUrl = await fillPDFProvincialGr78(form.getValues(), null);
    const { studentName, oen } = form.getValues();
    const fileName = [studentName, oen].filter(Boolean).join("_").replace(/\s+/g, " ").trim() || "provincial-report-card";
    const a = document.createElement("a");
    a.href = cleanUrl;
    a.setAttribute("download", `${fileName}.pdf`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNext = () => {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  };
  const handlePrev = () => {
    flushSaveRef.current?.();
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  };

  const toggleManualComplete = (stepId) => {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues('_completedFields') || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue('_completedFields', [...current]);
  };


  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((s) => s.id)
  );

  // ── Step content renderer ────────────────────────────────────────────────────
  const renderStep = () => {
    const { control } = form;

    // ── Step 1: Student Info ──────────────────────────────────────────────────
    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" />
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: "studentName",      label: "Student Name",       colSpan: 2 },
                { name: "oen",              label: "OEN"                            },
                { name: "date",             label: "Date"                           },
                { name: "grade",            label: "Grade",              small: true },
                { name: "gradeInSeptember", label: "Grade in Sept.",     small: true },
                { name: "teacher",          label: "Teacher",            colSpan: 2 },
              ].map(({ name, label, colSpan, small }) => (
                <FormField
                  key={name}
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className={colSpan === 2 ? "col-span-2" : ""}>
                      <FormLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {label}
                      </FormLabel>
                      <FormControl>
                        {small ? (
                          <input
                            {...field}
                            maxLength={3}
                            className="w-16 h-10 text-center text-base font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors block"
                          />
                        ) : (
                          <Input {...field} />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ── Step 2: School & Attendance ───────────────────────────────────────────
    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School & Attendance" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">School Details</p>
            <FormFieldGrid
              fields={[
                { name: "board",     label: "Board",          colSpan: 2 },
                { name: "school",    label: "School",         colSpan: 2 },
                { name: "address1",  label: "Address Line 1", colSpan: 2 },
                { name: "address2",  label: "Address Line 2", colSpan: 2 },
                { name: "principal", label: "Principal"                   },
                { name: "telephone", label: "Telephone"                   },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Attendance</p>
            <FormFieldGrid
              fields={[
                { name: "daysAbsent",      label: "Days Absent (Term)", inputMode: "numeric" },
                { name: "totalDaysAbsent", label: "Total Days Absent",  inputMode: "numeric" },
                { name: "timesLate",       label: "Times Late (Term)",  inputMode: "numeric" },
                { name: "totalTimesLate",  label: "Total Times Late",   inputMode: "numeric" },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
              gridClassName="grid grid-cols-2 gap-6"
            />
          </div>
        </div>
      );
    }

    // ── Step 3: Learning Skills (both terms) ──────────────────────────────────
    if (currentStep === 3) {
      const SKILLS = [
        { key: "responsibility",  label: "Responsibility"  },
        { key: "organization",    label: "Organization"    },
        { key: "independentWork", label: "Independent Work"},
        { key: "collaboration",   label: "Collaboration"   },
        { key: "initiative",      label: "Initiative"      },
        { key: "selfRegulation",  label: "Self-Regulation" },
      ];
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" subtitle="Ratings for both terms" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid items-center border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-teal-600 text-center">Term 1</span>
              <span />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">Term 2</span>
            </div>

            {SKILLS.map(({ key, label }, i) => (
              <div
                key={key}
                className={`grid items-center px-5 py-3 ${i < SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
              >
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <FormField control={control} name={`${key}1`} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="h-8 bg-slate-200" />
                <FormField control={control} name={`${key}2`} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                    </FormControl>
                  </FormItem>
                )} />
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <AICommentButton
              config={LSWH_CONFIG}
              form={form}
              fieldName="strengthsNextStepsLSWH"
              reportId={reportId}
              label="Strengths & Next Steps - Learning Skills"
              placeholder="Teacher comments on learning skills…"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2"
              textareaClassName="min-h-[100px] resize-none text-sm"
              buttonClassName="border-teal-200 text-teal-700 hover:bg-teal-50"
            />
          </div>
        </div>
      );
    }

    // ── Step 4: Language ──────────────────────────────────────────────────────
    if (currentStep === 4) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Language" />
          <SubjectCard
            title="Language"
            planField="languagePlan"
            grade1Field="languageGrade1"
            median1Field="languageMedian1"
            grade2Field="languageGrade2"
            median2Field="languageMedian2"
            commentsField="languageComments"
            plans={twoPlans}
            control={control}
          />
          <SubjectCard
            title="Native Language"
            nameField="nativeLanguage"
            planField="nativeLanguagePlan"
            grade1Field="nativeLanguageGrade1"
            median1Field="nativeLanguageMedian1"
            grade2Field="nativeLanguageGrade2"
            median2Field="nativeLanguageMedian2"
            commentsField="nativeLanguageComments"
            plans={twoPlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 5: French ────────────────────────────────────────────────────────
    if (currentStep === 5) {
      const FRENCH_SKILLS = [
        { label: "Listening", planField: "frenchListeningPlan", g1: "frenchListeningGrade1", m1: "frenchListeningMedian1", g2: "frenchListeningGrade2", m2: "frenchListeningMedian2" },
        { label: "Speaking",  planField: "frenchSpeakingPlan",  g1: "frenchSpeakingGrade1",  m1: "frenchSpeakingMedian1",  g2: "frenchSpeakingGrade2",  m2: "frenchSpeakingMedian2"  },
        { label: "Reading",   planField: "frenchReadingPlan",   g1: "frenchReadingGrade1",   m1: "frenchReadingMedian1",   g2: "frenchReadingGrade2",   m2: "frenchReadingMedian2"   },
        { label: "Writing",   planField: "frenchWritingPlan",   g1: "frenchWritingGrade1",   m1: "frenchWritingMedian1",   g2: "frenchWritingGrade2",   m2: "frenchWritingMedian2"   },
      ];
      return (
        <div className="space-y-4">
          <SectionHeading title="French" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">French Program Type</p>
            <FormField control={control} name="frenchPlan" render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PillSelector size="sm" value={field.value} onChange={field.onChange} options={frenchPlans} colorMap={{}} />
                </FormControl>
              </FormItem>
            )} />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "90px 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Plan (ESL/IEP)</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 Grade / Med · T2 Grade / Med</span>
            </div>
            {FRENCH_SKILLS.map(({ label, planField, g1, m1, g2, m2 }, i) => (
              <div
                key={planField}
                className={`grid items-center gap-4 px-5 py-3 ${i < FRENCH_SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "90px 1fr auto" }}
              >
                <span className="text-sm font-semibold text-slate-700">{label}</span>
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={twoPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[8px] font-bold text-teal-600 uppercase tracking-widest">T1</span>
                    <FormField control={control} name={g1} render={({ field }) => (
                      <FormItem>
                        <span className="text-[7px] text-slate-400 uppercase text-center block">Grade</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={control} name={m1} render={({ field }) => (
                      <FormItem>
                        <span className="text-[7px] text-slate-400 uppercase text-center block">Med</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                  </div>
                  <div className="w-px h-16 bg-slate-200 mt-4" />
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest">T2</span>
                    <FormField control={control} name={g2} render={({ field }) => (
                      <FormItem>
                        <span className="text-[7px] text-slate-400 uppercase text-center block">Grade</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={control} name={m2} render={({ field }) => (
                      <FormItem>
                        <span className="text-[7px] text-slate-400 uppercase text-center block">Med</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="frenchComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Comments</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="French program comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    // ── Step 6: Mathematics ───────────────────────────────────────────────────
    if (currentStep === 6) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Mathematics" />
          <SubjectCard
            title="Mathematics"
            planField="mathematicsPlan"
            grade1Field="mathematicsGrade1"
            median1Field="mathematicsMedian1"
            grade2Field="mathematicsGrade2"
            median2Field="mathematicsMedian2"
            commentsField="mathematicsComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 7: Science & Technology ─────────────────────────────────────────
    if (currentStep === 7) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Science & Technology" />
          <SubjectCard
            title="Science & Technology"
            planField="scienceAndTechnologyPlan"
            grade1Field="scienceAndTechnologyGrade1"
            median1Field="scienceAndTechnologyMedian1"
            grade2Field="scienceAndTechnologyGrade2"
            median2Field="scienceAndTechnologyMedian2"
            commentsField="scienceAndTechnologyComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 8: History & Geography ───────────────────────────────────────────
    if (currentStep === 8) {
      return (
        <div className="space-y-4">
          <SectionHeading title="History & Geography" />
          <SubjectCard
            title="History"
            planField="historyPlan"
            grade1Field="historyGrade1"
            median1Field="historyMedian1"
            grade2Field="historyGrade2"
            median2Field="historyMedian2"
            commentsField="historyComments"
            plans={fourPlans}
            control={control}
          />
          <SubjectCard
            title="Geography"
            planField="geographyPlan"
            grade1Field="geographyGrade1"
            median1Field="geographyMedian1"
            grade2Field="geographyGrade2"
            median2Field="geographyMedian2"
            commentsField="geographyComments"
            plans={fourPlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 9: Health & PE ───────────────────────────────────────────────────
    if (currentStep === 9) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Health & Physical Education" />

          {/* Health Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Health Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="healthEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades & Medians</p>
                <div className="flex items-start gap-4">
                  <GradeColumn control={control} gradeField="healthEducationGrade1" medianField="healthEducationMedian1" termLabel="Term 1" />
                  <div className="w-4 h-px bg-slate-200 mt-8" />
                  <GradeColumn control={control} gradeField="healthEducationGrade2" medianField="healthEducationMedian2" termLabel="Term 2" />
                </div>
              </div>
            </div>
          </div>

          {/* Physical Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Physical Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="physicalEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades & Medians</p>
                <div className="flex items-start gap-4">
                  <GradeColumn control={control} gradeField="physicalEducationGrade1" medianField="physicalEducationMedian1" termLabel="Term 1" />
                  <div className="w-4 h-px bg-slate-200 mt-8" />
                  <GradeColumn control={control} gradeField="physicalEducationGrade2" medianField="physicalEducationMedian2" termLabel="Term 2" />
                </div>
              </div>
            </div>
          </div>

          {/* Shared H&PE Comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="healthAndPhysicalEducationComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">
                  H&amp;PE Comments
                </FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Health & Physical Education comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    // ── Step 10: Arts & Optional Studies ─────────────────────────────────────
    if (currentStep === 10) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Arts" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsHeader" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Header</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. The Arts" className="max-w-xs" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "1fr 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subject / Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Program Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 Gr/Med · T2 Gr/Med</span>
            </div>
            {ARTS_ROWS.map(({ nameField, planField, grade1Field, median1Field, grade2Field, median2Field, placeholder }, i) => (
              <div
                key={nameField}
                className={`grid items-center gap-4 px-5 py-3 ${i < ARTS_ROWS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "1fr 1fr auto" }}
              >
                <FormField control={control} name={nameField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <input {...field} placeholder={placeholder}
                        className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-teal-400 focus:outline-none"
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-start gap-2">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[7px] font-bold text-teal-600 uppercase tracking-widest">T1</span>
                    <FormField control={control} name={grade1Field} render={({ field }) => (
                      <FormItem>
                        <span className="text-[6px] text-slate-400 uppercase text-center block">Gr</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={control} name={median1Field} render={({ field }) => (
                      <FormItem>
                        <span className="text-[6px] text-slate-400 uppercase text-center block">Med</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                  </div>
                  <div className="w-px h-16 bg-slate-200 mt-3" />
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[7px] font-bold text-slate-500 uppercase tracking-widest">T2</span>
                    <FormField control={control} name={grade2Field} render={({ field }) => (
                      <FormItem>
                        <span className="text-[6px] text-slate-400 uppercase text-center block">Gr</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={control} name={median2Field} render={({ field }) => (
                      <FormItem>
                        <span className="text-[6px] text-slate-400 uppercase text-center block">Med</span>
                        <FormControl>
                          <input {...field} maxLength={2}
                            className="w-10 h-8 text-center text-sm font-semibold border border-slate-200 rounded-lg bg-slate-50 focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Comments</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Arts comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Optional Studies */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Optional Studies</span>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
                <FormField control={control} name="optionalStudies" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Subject Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Islamic Studies" />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name="optionalStudiesPlan" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Program Plan</FormLabel>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-end gap-2 pb-0.5">
                  <FormField control={control} name="optionalStudiesGrade1" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name="optionalStudiesGrade2" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
              <FormField control={control} name="optionalStudiesComments" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Comments</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[80px] resize-none text-sm" />
                  </FormControl>
                </FormItem>
              )} />
            </div>
          </div>
        </div>
      );
    }

    
    return null;
  };

  const isShared = backUrl?.startsWith("/shared-classes");

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || "/"}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        <div className="text-center">
          <h1
            className="text-lg font-semibold text-slate-800"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Ontario Provincial Report Card - Gr. 7–8
          </h1>
          {contextInfo && (
            <p className="text-xs text-slate-500 leading-none mt-0.5">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear, contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {defaultsGroupId && (
            <Button size="sm" onClick={() => onRequestSave?.(form.getValues())} className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600">
              Save Defaults
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} className="gap-1.5">
            {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
            <span className="text-xs">{showPreview ? "Hide PDF" : "Show PDF"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class - changes you save are visible to the owner
        </div>
      )}

      {/* Class Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 border-b-2 border-blue-200 shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-blue-500" />
          <span className="text-sm font-medium text-blue-800">
            Class Defaults — <span className="font-normal">You&apos;re setting default values for all reports in this class. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? "w-[55%]" : "w-full"}`}>
          {/* Step indicator */}
          <div className="px-5 pt-4 pb-2 shrink-0 border-b border-slate-100 bg-white">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && !defaultsGroupId && (
            <div className="px-5 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className="flex-1 overflow-y-auto px-5 py-5 bg-slate-50">
            <Form {...form}>
              <form>{renderStep()}</form>
            </Form>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>
            <div className="flex items-center gap-2">
              {!isShared && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? "default" : "outline"}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? "gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : "gap-1.5"}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? "Marked Complete" : "Mark Complete"}
                </Button>
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                  <LucideDownload size={16} className="mr-1.5" />
                  {isPdfGenerating ? "Updating…" : "Download PDF"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${showPreview ? "w-[45%]" : "w-0"}`}>
          {showPreview && (
            <PDFPreview src={filledPDF} scrollTarget={scrollTargets[currentStep]} defaultPageCount={4} />
          )}
        </div>
      </div>
    </div>
  );
};
